import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/layout/Sidebar";
import TierDistributionChart from "../components/loyalty/TierDistributionChart";
import TierCard from "../components/loyalty/TierCard";
import Modal from "../components/layout/Modal";
import DeleteModal from "../components/layout/DeleteModal";
import LoyaltyForm, { LoyaltyFormValues } from "../components/loyalty/LoyaltyForm";
import ConfigurePointsForm from "../components/loyalty/ConfigurePointsForm";
import EmptyState from "../components/ui/EmptyState";
import { useLoyaltyTiers, useUpdateLoyaltyTier, useDeleteLoyaltyTier, LoyaltyTier } from "../services/loyalty";
import ROUTES from "../utils/ROUTES";
import AddNewButton from "../components/ui/AddNewButton";
import { toast } from "../hooks/use-toast";
import axios from "axios";

const getErrorMessage = (err: unknown): string =>
  axios.isAxiosError(err)
    ? err.response?.data?.error ?? err.message
    : "Something went wrong. Please try again.";

const Loyalty = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [editingTier, setEditingTier] = useState<LoyaltyTier | null>(null);
  const [deletingTier, setDeletingTier] = useState<LoyaltyTier | null>(null);
  const [configurePointsOpen, setConfigurePointsOpen] = useState(false);

  const { data: tiers, isLoading, isError } = useLoyaltyTiers();
  const { mutate: updateTier } = useUpdateLoyaltyTier();
  const { mutate: deleteTier } = useDeleteLoyaltyTier();

  const handleConfirmDelete = () => {
    if (!deletingTier) return;
    deleteTier(deletingTier.id, {
      onSuccess: () => setDeletingTier(null),
      onError: (err) => {
        setDeletingTier(null);
        toast({
          variant: "error",
          title: "Failed to delete tier",
          description: getErrorMessage(err),
        });
      },
    });
  };

  const handleSave = (values: LoyaltyFormValues) => {
    if (!editingTier) return;
    updateTier(
      { id: editingTier.id, ...values },
      {
        onSuccess: () => { toast({ variant: "success", title: "Loyalty tier updated", description: "The loyalty tier has been successfully updated." }); setEditingTier(null); },
        onError: (err) => { toast({ variant: "error", title: "Failed to update loyalty tier", description: getErrorMessage(err) }); },
      }
    );
  };

  return (
    <div className="flex h-screen overflow-hidden bg-surface-page">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <main className="flex-1 min-w-0 overflow-y-auto px-4 sm:px-8 lg:pl-11 lg:pr-8 pt-6 lg:pt-[60px] pb-16">
        {/* Mobile hamburger */}
        <div className="flex items-center justify-between mb-4 lg:hidden">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg hover:bg-black/5 transition-colors"
            aria-label="Open menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="#012926" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>

        {/* Page title row */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1
              className="text-[28px] sm:text-[34px] uppercase tracking-[0.68px] leading-none text-brand-dark"
              style={{ fontFamily: '"Bebas Neue", sans-serif' }}
            >
              Loyalty
            </h1>
            <p className="mt-3 text-[14px] sm:text-[16px] font-medium text-content-label">
              Real-time overview of program health and customer engagement.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setConfigurePointsOpen(true)}
              className="shrink-0 px-10 py-[10px] rounded-[20px] text-[16px] font-bold tracking-[0.64px] border border-brand-primary text-brand-primary transition-opacity hover:opacity-70"
            >
              Configure Points
            </button>
            <AddNewButton onClick={() => navigate(ROUTES.ADD_LOYALTY)} />
          </div>
        </div>

        {/* Content */}
        <div className="mt-8 lg:mt-[36px] flex flex-col gap-8">
          {isLoading && <p className="text-[14px] text-content-label">Loading…</p>}
          {isError && <p className="text-[14px] text-content-error">Failed to load loyalty tiers.</p>}
          {!isLoading && !isError && (
            <>
              <p className="text-[14px] font-normal" style={{ color: "#034d48" }}>
                {tiers?.length ?? 0} Tiers Active
              </p>

              <TierDistributionChart />

              {!tiers?.length ? (
                <EmptyState message="No loyalty tiers found." />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {tiers.map((tier) => (
                    <TierCard
                      key={tier.id}
                      name={tier.name}
                      iconBg={tier.color}
                      pointsRange={`From ${tier.min_points.toLocaleString()} PTS`}
                      onEdit={() => setEditingTier(tier)}
                      onDelete={tier.min_points === 0 ? undefined : () => setDeletingTier(tier)}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <Modal
        isOpen={configurePointsOpen}
        onClose={() => setConfigurePointsOpen(false)}
        title="Configure Points"
      >
        <ConfigurePointsForm
          onSuccess={() => setConfigurePointsOpen(false)}
        />
      </Modal>

      <Modal
        isOpen={editingTier !== null}
        onClose={() => setEditingTier(null)}
        title={`Edit ${editingTier?.name ?? ""} Tier`}
      >
        <LoyaltyForm
          initialValues={{
            name: editingTier?.name,
            min_points: editingTier?.min_points,
            color: editingTier?.color,
          }}
          disableMinPoints={editingTier?.min_points === 0}
          onSave={handleSave}
        />
      </Modal>

      <DeleteModal
        isOpen={deletingTier !== null}
        onClose={() => setDeletingTier(null)}
        onConfirm={handleConfirmDelete}
        title="Are you sure you want to delete this loyalty?"
        message={
          <>
            This action will permanently remove the{" "}
            <span
              className="font-semibold text-content-body"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              &ldquo;{deletingTier?.name}&rdquo;
            </span>{" "}
            loyalty from your system. This action cannot be undone.
          </>
        }
        confirmLabel="Delete Loyalty"
        cancelLabel="Cancel"
      />
    </div>
  );
};

export default Loyalty;
